'use client';

/**
 * Cardiac Age card — biological cardiovascular age estimate.
 *
 * Maps RHR baseline + HRV baseline vs age-adjusted population norms
 * to a single number: "your cardiovascular system reads X years."
 *
 * Self-gates: hidden when confidence < 0.5 (we need at least 14 days
 * of HRV or RHR baseline to make a credible age claim — anything
 * less and the bar shifts week to week, which is misleading).
 *
 * Tap → /metrics for the full breakdown (future SITREP wiring).
 */

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Heart, Activity, ArrowRight } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { loadCardiacAge, type CardiacAgeResult } from '@/lib/cardiacAge';

export default function CardiacAgeCard() {
  const router = useRouter();
  const { user } = useAuth();
  const [result, setResult] = useState<CardiacAgeResult | null>(null);

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await loadCardiacAge(user.uid);
        if (!cancelled) setResult(r);
      } catch (e) {
        // loadCardiacAge already catches internally, but belt-and-
        // suspenders: any unexpected throw here must not surface as an
        // uncaught promise rejection that crashes the Android WebView.
        console.warn('[CardiacAgeCard] load failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Defensive shape validation. A malformed result (e.g. a stale cache
  // from a previous version with a different shape) used to throw at
  // render time — `.contributors.map` on undefined, `.toFixed` on a
  // non-number — which on Android took the whole dashboard down. Bail
  // quietly instead.
  if (
    !result ||
    !Number.isFinite(result.confidence) ||
    !Number.isFinite(result.cardiacAge) ||
    !Number.isFinite(result.shift) ||
    !Array.isArray(result.contributors) ||
    result.confidence < 0.5
  ) {
    return null;
  }

  const tone =
    result.shift >= 5 ? { accent: 'text-emerald-300', border: 'border-emerald-500/50', glow: 'shadow-[0_0_36px_-12px_rgba(74,222,128,0.5)]' } :
    result.shift >= 1.5 ? { accent: 'text-emerald-300', border: 'border-emerald-500/40', glow: '' } :
    result.shift >= -1.5 ? { accent: 'text-hud-cyan', border: 'border-hud-cyan/40', glow: '' } :
    result.shift >= -5 ? { accent: 'text-amber-300', border: 'border-amber-500/40', glow: '' } :
                          { accent: 'text-red-300', border: 'border-red-500/50', glow: '' };

  return (
    <button
      onClick={() => router.push('/metrics')}
      className={`group relative w-full text-left rounded-md bg-hud-ink/80 border ${tone.border} ${tone.glow} overflow-hidden`}
    >
      <span aria-hidden className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-50 ${tone.accent}`} />
      <span aria-hidden className={`absolute inset-y-0 left-0 w-0.5 opacity-80 ${tone.accent}`} />

      <div className="relative px-3 py-2.5 pl-4 space-y-2">
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Heart className={`w-3.5 h-3.5 ${tone.accent}`} aria-hidden />
            <span className={`font-mono text-[10px] uppercase tracking-[0.32em] font-bold ${tone.accent}`}>
              Cardiac age
            </span>
          </div>
          <ArrowRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 transition" aria-hidden />
        </div>

        {/* Big number */}
        <div className="flex items-baseline gap-2">
          <span className={`font-heading text-4xl font-black tabular-nums ${tone.accent}`}>
            {Math.round(result.cardiacAge)}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            yrs
          </span>
          {result.shift !== 0 && (
            <span className={`ml-auto font-mono text-[10px] uppercase tracking-[0.22em] font-bold tabular-nums ${tone.accent}`}>
              {result.shift > 0 ? '−' : '+'}{Math.abs(result.shift).toFixed(1)} vs chrono
            </span>
          )}
        </div>

        {/* Headline */}
        <p className="text-xs text-slate-300 leading-snug">{result.headline}</p>

        {/* Contributor chips */}
        <div className="grid grid-cols-2 gap-1.5">
          {result.contributors.filter(c =>
            c &&
            Number.isFinite(c.deltaYears) &&
            Number.isFinite(c.userValue) &&
            Number.isFinite(c.expected),
          ).map((c) => {
            const Icon = c.metric === 'hrv' ? Heart : Activity;
            const dirTw =
              c.direction === 'younger' ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.06]' :
              c.direction === 'older' ? 'text-red-300 border-red-500/40 bg-red-500/[0.06]' :
                                          'text-slate-400 border-operator-steel/40 bg-operator-void/40';
            return (
              <div key={c.metric} className={`px-2 py-1 rounded-sm border ${dirTw}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Icon className="w-2.5 h-2.5" aria-hidden />
                    {/* The window is part of the label, not decoration.
                        These are 30-DAY BASELINES. Recovery Quality shows
                        TODAY's reading under the same "HRV" / "RHR" names, so
                        on a day with no wearable reading the two cards sat
                        inches apart saying "HRV · NO READING" and "HRV 68 MS"
                        — one number, two windows, apparent contradiction. */}
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] font-bold">
                      {c.metric === 'hrv' ? 'HRV' : 'RHR'} <span className="text-slate-500">· 30D AVG</span>
                    </span>
                  </div>
                  <span className="font-mono text-[9px] tabular-nums">
                    {c.deltaYears > 0 ? '−' : '+'}{Math.abs(c.deltaYears)}y
                  </span>
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] mt-0.5 truncate">
                  {c.userValue} {c.metric === 'hrv' ? 'ms' : 'bpm'} <span className="text-slate-600">vs {c.expected}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Confidence footer */}
        <div className="flex items-center justify-between pt-1 border-t border-operator-steel/30 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600">
          <span>Confidence · {(result.confidence * 100).toFixed(0)}%</span>
          <span className="hidden sm:inline">Not a clinical assessment</span>
        </div>
      </div>
    </button>
  );
}
